import { AutocompleteToken } from "./autocompleteTokens";

/**
 * Type representing valid autocomplete attribute values
 */
export type AutocompleteValue = false | "off" | "on" | AutocompleteToken | string; // For complex token combinations

/**
 * Validates and normalizes an autocomplete value for use in HTML attributes
 *
 * @param value - The autocomplete value to process
 * @returns The normalized HTML autocomplete attribute value or undefined if invalid
 */
export function normalizeAutocompleteValue(value: AutocompleteValue | undefined): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (value === false) {
    return "off";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  }

  return undefined;
}

/**
 * Checks if an autocomplete token is valid according to HTML specification
 * This is a basic validation - more complex token combinations may require additional validation
 *
 * @param token - The autocomplete token to validate
 * @returns True if the token appears to be valid
 */
export function isValidAutocompleteToken(token: string): boolean {
  if (!token || typeof token !== "string") {
    return false;
  }

  const trimmed = token.trim().toLowerCase();

  // Special cases
  if (trimmed === "off" || trimmed === "on") {
    return true;
  }

  // Basic token pattern validation: This is a simplified check - the full spec allows for more complex combinations
  const tokenPattern = /^(section-[a-zA-Z0-9_-]+\s+)?(shipping\s+|billing\s+)?[a-zA-Z][a-zA-Z0-9-]*(\s+(home|work|mobile|fax|pager))?$/;

  return tokenPattern.test(trimmed);
}

/**
 * Creates a scoped autocomplete token with section and/or address context
 *
 * @param fieldName - The base field name (e.g., "email", "street-address")
 * @param options - Optional scoping parameters
 * @returns The formatted autocomplete token string
 *
 * @example
 * ```typescript
 * // Basic field
 * createScopedToken("email") // "email"
 *
 * // With section
 * createScopedToken("email", { section: "user" }) // "section-user email"
 *
 * // With address context
 * createScopedToken("street-address", { addressContext: "billing" }) // "billing street-address"
 *
 * // With contact type
 * createScopedToken("tel", { contactType: "work" }) // "tel work"
 *
 * // Complex combination
 * createScopedToken("street-address", {
 *   section: "checkout",
 *   addressContext: "shipping"
 * }) // "section-checkout shipping street-address"
 * ```
 */
export function createScopedToken(
  fieldName: AutocompleteToken | string,
  options?: {
    section?: string;
    addressContext?: "shipping" | "billing";
    contactType?: "home" | "work" | "mobile" | "fax" | "pager";
  },
): string {
  const parts: string[] = [];

  if (options?.section) {
    parts.push(`section-${options.section}`);
  }

  if (options?.addressContext) {
    parts.push(options.addressContext);
  }

  parts.push(fieldName);

  if (options?.contactType) {
    parts.push(options.contactType);
  }

  return parts.join(" ");
}

/**
 * Common autocomplete token combinations for convenience
 */
export const COMMON_AUTOCOMPLETE_PATTERNS = {
  // Personal Information
  FULL_NAME: "name",
  FIRST_NAME: "given-name",
  LAST_NAME: "family-name",
  EMAIL: "email",
  PHONE: "tel",
  WORK_PHONE: "tel work",
  MOBILE_PHONE: "tel mobile",

  // Authentication
  CURRENT_PASSWORD: "current-password",
  NEW_PASSWORD: "new-password",
  USERNAME: "username",

  // Billing Address
  BILLING_NAME: "billing name",
  BILLING_ADDRESS_LINE1: "billing address-line1",
  BILLING_ADDRESS_LINE2: "billing address-line2",
  BILLING_CITY: "billing address-level2",
  BILLING_STATE: "billing address-level1",
  BILLING_ZIP: "billing postal-code",
  BILLING_COUNTRY: "billing country",

  // Shipping Address
  SHIPPING_NAME: "shipping name",
  SHIPPING_ADDRESS_LINE1: "shipping address-line1",
  SHIPPING_ADDRESS_LINE2: "shipping address-line2",
  SHIPPING_CITY: "shipping address-level2",
  SHIPPING_STATE: "shipping address-level1",
  SHIPPING_ZIP: "shipping postal-code",
  SHIPPING_COUNTRY: "shipping country",

  // Credit Card
  CC_NUMBER: "cc-number",
  CC_NAME: "cc-name",
  CC_EXPIRY: "cc-exp",
  CC_EXPIRY_MONTH: "cc-exp-month",
  CC_EXPIRY_YEAR: "cc-exp-year",
  CC_SECURITY_CODE: "cc-csc",

  // Dates
  BIRTH_DATE: "bday",
  BIRTH_DAY: "bday-day",
  BIRTH_MONTH: "bday-month",
  BIRTH_YEAR: "bday-year",

  // Organization
  COMPANY: "organization",
  JOB_TITLE: "organization-title",

  // URLs and handles
  WEBSITE: "url",

  // Special
  DISABLE: "off",
  ENABLE: "on",
} as const;

/**
 * Type for common autocomplete patterns
 */
export type CommonAutocompletePattern = (typeof COMMON_AUTOCOMPLETE_PATTERNS)[keyof typeof COMMON_AUTOCOMPLETE_PATTERNS];
